import { useCallback, useRef, useState } from "react";
import { apiUpload } from "../../lib/api-client";
import { UploadedDocument, W3WizardStatus, uploadPlan, toggleDocumentExclusion } from "../../lib/api/w3-wizard";

interface TicketUploaderProps {
  sessionId: string;
  existingDocuments: UploadedDocument[];
  onUploadComplete: (documents: UploadedDocument[]) => void;
  onDocumentsUpdated: (documents: UploadedDocument[]) => void;
  onError: (error: string) => void;
  hasPlanSnapshot: boolean;
  planImporting: boolean;
  onPlanUploadComplete: (documents: UploadedDocument[]) => void;
  sessionStatus?: W3WizardStatus;
}

const ACCEPTED_EXTENSIONS = ".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.csv,.xlsx,.xls";
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

function getFileTypeBadge(file: File): { label: string; className: string } {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mime = file.type;
  if (mime === "application/pdf" || ext === "pdf") {
    return { label: "PDF", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
  }
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "tiff"].includes(ext)) {
    return { label: "Image", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" };
  }
  if (["docx", "doc"].includes(ext) || mime.includes("word")) {
    return { label: "DOCX", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" };
  }
  if (["csv", "xlsx", "xls"].includes(ext) || mime.includes("excel") || mime.includes("spreadsheet") || mime === "text/csv") {
    return { label: ext.toUpperCase() || "CSV", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" };
  }
  return { label: ext.toUpperCase() || "FILE", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400" };
}

function getExistingDocTypeBadge(fileType: string): { label: string; className: string } {
  const t = fileType.toLowerCase();
  if (t === "pdf") return { label: "PDF", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
  if (["jpg", "jpeg", "png", "tiff", "image"].some((s) => t.includes(s))) {
    return { label: "Image", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" };
  }
  if (["docx", "doc", "word"].some((s) => t.includes(s))) {
    return { label: "DOCX", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" };
  }
  if (["csv", "xlsx", "xls", "excel", "spreadsheet"].some((s) => t.includes(s))) {
    return { label: t.toUpperCase(), className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" };
  }
  return { label: fileType, className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400" };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TicketUploader({
  sessionId,
  existingDocuments,
  onUploadComplete,
  onDocumentsUpdated,
  onError,
  hasPlanSnapshot,
  planImporting,
  onPlanUploadComplete,
  sessionStatus,
}: TicketUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggleExclusion = async (doc: UploadedDocument) => {
    const newExcluded = !doc.is_excluded;
    // Optimistic update
    const updatedDocs = existingDocuments.map((d) =>
      d.storage_key === doc.storage_key ? { ...d, is_excluded: newExcluded } : d
    );
    onDocumentsUpdated(updatedDocs);

    try {
      await toggleDocumentExclusion(sessionId, doc.storage_key, newExcluded);
    } catch {
      // Revert on error
      onDocumentsUpdated(existingDocuments);
      onError("Failed to update document exclusion");
    }
  };

  // Plan upload state
  const [planFile, setPlanFile] = useState<File | null>(null);
  const [planUploading, setPlanUploading] = useState(false);
  const [isPlanDragOver, setIsPlanDragOver] = useState(false);
  const planInputRef = useRef<HTMLInputElement>(null);

  const PLAN_ACCEPTED_EXTENSIONS = ".docx,.doc,.pdf";
  const PLAN_ACCEPTED_MIME_TYPES = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/pdf",
  ];

  const handlePlanDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsPlanDragOver(false);
    const dropped = e.dataTransfer.files;
    if (!dropped || dropped.length === 0) return;
    const file = dropped[0];
    if (
      PLAN_ACCEPTED_MIME_TYPES.includes(file.type) ||
      PLAN_ACCEPTED_EXTENSIONS.split(",").some((ext) => file.name.toLowerCase().endsWith(ext.replace(".", "")))
    ) {
      setPlanFile(file);
    }
  }, []);

  const handlePlanUpload = async () => {
    if (!planFile) return;
    setPlanUploading(true);
    try {
      const data = await uploadPlan(sessionId, planFile);
      setPlanFile(null);
      onPlanUploadComplete(data.documents);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Plan upload failed");
    } finally {
      setPlanUploading(false);
    }
  };

  const ticketDocuments = existingDocuments.filter((d) => d.category !== "plan");
  const planDocument = existingDocuments.find((d) => d.category === "plan");

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const valid = Array.from(incoming).filter((f) =>
      ACCEPTED_MIME_TYPES.includes(f.type) ||
      ACCEPTED_EXTENSIONS.split(",").some((ext) => f.name.toLowerCase().endsWith(ext.replace(".", "")))
    );
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !existing.has(f.name))];
    });
  };

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(10);

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("category", "tickets");

      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 10, 85));
      }, 1500);

      const data = await apiUpload<{ uploaded_count: number; documents: UploadedDocument[]; session_status: string }>(
        `/api/w3-wizard/${sessionId}/upload/`,
        formData
      );

      clearInterval(progressInterval);
      setProgress(100);
      setFiles([]);
      onUploadComplete(data.documents);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-4">
      {/* ---- Plan Section ---- */}
      <div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
          Approved Plan
        </p>

        {hasPlanSnapshot && !planImporting && sessionStatus !== 'plan_imported' ? (
          /* Plan verified/ready — green state */
          <div className="flex items-center gap-3 rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/10 px-4 py-3">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="min-w-0">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">Approved plan loaded</p>
              {planDocument && (
                <p className="text-xs text-green-600 dark:text-green-400 truncate mt-0.5">{planDocument.file_name}</p>
              )}
            </div>
          </div>
        ) : hasPlanSnapshot && !planImporting && sessionStatus === 'plan_imported' ? (
          /* Plan imported but awaiting verification — amber state */
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-4 py-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Plan imported — awaiting verification</p>
              {planDocument && (
                <p className="text-xs text-amber-600 dark:text-amber-400 truncate mt-0.5">{planDocument.file_name}</p>
              )}
            </div>
          </div>
        ) : planImporting ? (
          /* Plan importing in progress */
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-4 py-3">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-amber-600 border-r-transparent flex-shrink-0" />
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Importing plan...</p>
          </div>
        ) : (
          /* Plan upload drop zone */
          <div className="space-y-2">
            <div
              onDrop={handlePlanDrop}
              onDragOver={(e) => { e.preventDefault(); setIsPlanDragOver(true); }}
              onDragLeave={() => setIsPlanDragOver(false)}
              onClick={() => planInputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
                isPlanDragOver
                  ? "border-brand-500 bg-brand-50 dark:bg-brand-900/10"
                  : "border-gray-300 hover:border-brand-400 dark:border-white/[0.15] dark:hover:border-brand-500/60"
              }`}
            >
              <input
                ref={planInputRef}
                type="file"
                accept={PLAN_ACCEPTED_EXTENSIONS}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setPlanFile(f);
                }}
              />
              <div className="flex flex-col items-center gap-1.5">
                <svg className="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {planFile ? planFile.name : "Drop approved plan here or click to browse"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">DOCX or PDF — single file</p>
              </div>
            </div>
            {planFile && (
              <button
                onClick={handlePlanUpload}
                disabled={planUploading}
                className="w-full py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {planUploading ? "Uploading plan..." : "Upload Plan"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200 dark:bg-white/[0.08]" />
        <span className="text-xs text-gray-400 dark:text-gray-500 font-medium uppercase">Daily Tickets</span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-white/[0.08]" />
      </div>

      {/* Existing ticket documents */}
      {ticketDocuments.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
            Uploaded Tickets ({ticketDocuments.length})
          </p>
          <div className="rounded-xl border border-gray-200 dark:border-white/[0.05] divide-y divide-gray-100 dark:divide-white/[0.05]">
            {ticketDocuments.map((doc) => {
              const badge = getExistingDocTypeBadge(doc.file_type);
              return (
                <div
                  key={doc.storage_key}
                  className={`flex items-center justify-between px-4 py-2.5 transition-colors ${
                    doc.is_excluded
                      ? "bg-gray-50 dark:bg-gray-800/30 opacity-60"
                      : "bg-green-50/40 dark:bg-green-900/5"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      type="checkbox"
                      checked={!doc.is_excluded}
                      onChange={() => handleToggleExclusion(doc)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 flex-shrink-0"
                      title={doc.is_excluded ? "Include this document" : "Exclude this document"}
                    />
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badge.className}`}>
                      {badge.label}
                    </span>
                    <span className={`text-sm truncate ${doc.is_excluded ? "text-gray-400 line-through" : "text-gray-700 dark:text-gray-300"}`}>
                      {doc.file_name}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {formatBytes(doc.size_bytes)}
                    </span>
                  </div>
                  <span className={`text-xs flex-shrink-0 ml-3 ${
                    doc.is_excluded
                      ? "text-gray-400 dark:text-gray-500"
                      : "text-green-600 dark:text-green-400"
                  }`}>
                    {doc.is_excluded ? "Excluded" : "Uploaded"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          isDragOver
            ? "border-brand-500 bg-brand-50 dark:bg-brand-900/10"
            : "border-gray-300 hover:border-brand-400 dark:border-white/[0.15] dark:hover:border-brand-500/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="flex flex-col items-center gap-2">
          <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Drag &amp; drop files here or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PDF, DOCX, Images (JPG/PNG/TIFF), Excel, CSV
          </p>
        </div>
      </div>

      {/* Pending file list */}
      {files.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-white/[0.05] divide-y divide-gray-100 dark:divide-white/[0.05]">
          {files.map((f) => {
            const badge = getFileTypeBadge(f);
            return (
              <div key={f.name} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badge.className}`}>
                    {badge.label}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{f.name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {formatBytes(f.size)}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                  disabled={uploading}
                  className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40 ml-3"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress bar */}
      {uploading && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Uploading documents...</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-brand-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload button */}
      <button
        onClick={handleUpload}
        disabled={files.length === 0 || uploading}
        className="w-full py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {uploading
          ? "Uploading..."
          : `Upload ${files.length > 0 ? `${files.length} File${files.length !== 1 ? "s" : ""}` : "Files"}`}
      </button>
    </div>
  );
}
